import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  ArrowRight, ArrowUpRight, Plus, Minus, Play, Check,
  QrCode, CalendarRange, Receipt, FileSignature, Sparkles, Wallet,
  ChevronLeft, ChevronRight, Shield, Clock,
  ClipboardList, LayoutDashboard,
  MessageCircle, ShieldCheck, Megaphone,
  Bell, Mail, Smartphone,
  Gift, Award, Star, Trophy, Heart, GraduationCap,
  Globe, ScanLine, Brain,
  UserPlus, Lock, CreditCard, FolderLock, Plane, Home, Bot, Layers,
  Sun, Moon, Menu, X, FileText,
} from "lucide-react";
import { VerixaeLockup } from "../components/Logo";
import { DemoRequestModal } from "../components/DemoRequestModal";
import { InteractiveTourModal } from "../components/InteractiveTourModal";
import { BackToTop } from "../components/Interactions";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toast } from "sonner";
import { EnterpriseQuoteModal } from "../components/EnterpriseQuoteModal";
import { LanguageSwitcher } from "../components/LanguageSwitcher";

/* =========================================================
   VERIXA · Landing v4 — LIGHT PROFESSIONAL
   - White background, Inter font, corporate tone
   - Single unified service carousel (auto-scroll)
   - Google Calendar pro showcase
   - Testimonials without company name
   - Pricing horizontal carousel with arrows
   ========================================================= */

const SERVICES = [
  {
    id: "cv", title: "CV × Commessa AI", kicker: "ANALISI DI ASSOCIAMENTO",
    desc: "I CV sono già nella tua libreria. Incolli una nuova commessa e l'AI calcola in 3 secondi il punteggio di congruenza per ogni profilo, con punti di forza e lacune. Nessun upload, nessun lavoro manuale: il match è automatico.",
    icon: FileText, accent: "#6366F1", aiGenerated: false, isNew: true,
    video: null,
    poster: "/services/cv-hero.jpg",
    bullets: ["Match su libreria CV esistente", "Analisi strengths & gaps GPT-5", "Punteggio congruenza immediato"],
    deepLink: "/app/cv?tab=match",
  },
  {
    id: "onboarding", title: "Onboarding 30-60-90", kicker: "PERCORSO GUIDATO NUOVI ASSUNTI",
    desc: "Ogni nuovo assunto ha un percorso personalizzato di 30, 60 e 90 giorni: checklist, firma documenti con valore legale, assegnazione buddy, formazione obbligatoria e survey di soddisfazione. L'HR vede il progresso in tempo reale.",
    icon: GraduationCap, accent: "#F97316", aiGenerated: false, isNew: true,
    video: null,
    poster: "/services/onboarding-hero.jpg",
    bullets: ["Checklist 30 · 60 · 90 giorni", "Firma documenti eIDAS integrata", "Survey + buddy assignment"],
  },
  {
    id: "ai", title: "AI Insights", kicker: "GPT-5 IN ITALIANO",
    desc: "Il cuore del gestionale: GPT-5 in italiano analizza i tuoi dati, rileva anomalie, prevede sovraccarichi e produce report on-demand. Un consulente che non dorme mai.",
    icon: Sparkles, accent: "#A855F7", aiGenerated: false,
    video: null,
    poster: "/services/ai-hero.jpg",
    bullets: ["Anomalie automatiche", "Previsioni carico lavoro", "Report in linguaggio naturale"],
  },
  {
    id: "dashboard", title: "Dashboard AI", kicker: "COCKPIT DELL'IMPRESA",
    desc: "Una dashboard che si configura da sola in base al tuo ruolo: KPI finanziari, operativi e HR in un'unica vista. Alert proattivi e drill-down su ogni numero.",
    icon: LayoutDashboard, accent: "#10B981", aiGenerated: false,
    video: null,
    poster: "/services/dashboard-hero.jpg",
    bullets: ["KPI cross-funzionali", "Alert proattivi smart", "Heatmap presenze e ferie"],
  },
  {
    id: "blockchain", title: "Blockchain comunicazioni", kicker: "AUDIT IMMUTABILE",
    desc: "Ogni comunicazione ufficiale (contratto, busta paga, lettera disciplinare) viene hashata su blockchain. Prova legale di invio, ricezione e contenuto. Inalterabile.",
    icon: ShieldCheck, accent: "#0F766E", aiGenerated: false,
    video: null,
    poster: "/services/blockchain-hero.jpg",
    bullets: ["Hash SHA-256 on-chain", "Prova legale eIDAS+", "Verifica pubblica del documento"],
  },
  {
    id: "firma", title: "Firma digitale", kicker: "VALORE LEGALE EIDAS",
    desc: "Canvas touch + hash SHA-256 crittografico. Conforme a eIDAS, archiviazione decennale inclusa. Firma massiva fino a 500 destinatari in un click.",
    icon: FileSignature, accent: "#6366F1", aiGenerated: false,
    video: null,
    poster: "/services/firma-hero.jpg",
    bullets: ["SHA-256 hash", "Audit log GDPR", "PDF/A archive"],
  },
  {
    id: "broadcast", title: "Invio massivo", kicker: "FINO A 20.000 PERSONE",
    desc: "Comunicazioni a tutta l'azienda — anche multi-sede — in un solo click. Push, email, SMS e WhatsApp Business in parallelo. Rapporto di lettura in tempo reale.",
    icon: Megaphone, accent: "#DC2626", aiGenerated: false,
    video: null,
    poster: "/services/broadcast-hero.jpg",
    bullets: ["Push + Email + SMS + WhatsApp", "Segmentazione per sede/ruolo", "Read receipts in tempo reale"],
  },
  {
    id: "cral", title: "CRAL & Buoni sconto", kicker: "WELFARE AZIENDALE",
    desc: "Convenzioni esclusive con palestre, cinema, supermercati, viaggi e benessere. I dipendenti accedono ai buoni sconto direttamente dall'app, l'azienda non gestisce nulla.",
    icon: Gift, accent: "#F472B6", aiGenerated: false,
    video: null,
    poster: "/services/cral-hero.jpg",
    bullets: ["+500 partner convenzionati", "Buoni sconto in 1 click", "Welfare detraibile fiscalmente"],
  },
  {
    id: "fedelta", title: "Carta Fedeltà Punti", kicker: "GAMIFICATION DIPENDENTI",
    desc: "Ogni dipendente ha la sua carta digitale: guadagna punti su presenza, puntualità e obiettivi. 27 punti/mese a condotta piena, convertibili in welfare, gadget o buoni.",
    icon: Award, accent: "#EAB308", aiGenerated: false,
    video: null,
    poster: "/services/fedelta-hero.jpg",
    bullets: ["27 punti/mese presenza piena", "Bonus puntualità + obiettivi", "Riscatti in catalogo CRAL"],
  },
  {
    id: "chat", title: "Chat aziendale", kicker: "TEMPO REALE · END-TO-END",
    desc: "Messaggi diretti e canali di team in tempo reale. Allegati, reazioni, ricerca, presenza online. Sostituisce WhatsApp aziendale senza pagare per utente.",
    icon: MessageCircle, accent: "#EC4899", aiGenerated: false,
    video: null,
    poster: "/services/chat-hero.jpg",
    bullets: ["DM + canali pubblici/privati", "Allegati e ricerca full-text", "Web push + PWA mobile"],
  },
  {
    id: "calendario", title: "Calendario AI", kicker: "SYNC GOOGLE · OUTLOOK",
    desc: "Sincronizzazione bidirezionale con Google Calendar e Outlook. L'AI previene sovrapposizioni e suggerisce orari ottimali per riunioni, turni e trasferte.",
    icon: CalendarRange, accent: "#7C3AED", aiGenerated: false,
    video: null,
    poster: "/services/calendario-hero.jpg",
    bullets: ["Sync Google · Outlook", "Suggerimenti GPT-5", "Festività italiane"],
  },
  {
    id: "rimborsi", title: "Rimborsi spese", kicker: "OCR ITALIANO",
    desc: "Carica lo scontrino, l'OCR estrae i dati, approvi in un click. Flusso medio: 11 secondi. Export pronto per il commercialista o il software paghe.",
    icon: Receipt, accent: "#06B6D4", aiGenerated: false,
    video: null,
    poster: "/services/rimborsi-hero.jpg",
    bullets: ["OCR italiano", "Multi-categoria", "Export paghe CSV"],
  },
  {
    id: "paghe", title: "Cedolini & Paghe", kicker: "EXPORT ZUCCHETTI · TS · INAZ",
    desc: "Export pronto per Zucchetti, TeamSystem, Inaz. Causali e totali pre-mappati automaticamente. Il tuo studio paghe non deve toccare un file.",
    icon: Wallet, accent: "#0EA5E9", aiGenerated: false,
    video: null,
    poster: "/services/paghe-hero.jpg",
    bullets: ["Zucchetti · TS · Inaz", "Causali automatiche", "Storico illimitato"],
  },
  {
    id: "rapportino", title: "Rapportino Ore AI", kicker: "TIMESHEET AUTOMATICO",
    desc: "Le timbrature compilano da sole il rapportino ore. Tu controlli, modifichi se serve, esporti in PDF o CSV per il cliente in un click.",
    icon: ClipboardList, accent: "#F59E0B", aiGenerated: false,
    video: null,
    poster: "/services/rapportino-hero.jpg",
    bullets: ["Da QR a rapportino: 0 click", "Modifica manuale opzionale", "Export PDF + CSV cliente"],
  },
  {
    id: "presenze", title: "Presenze QR + GPS", kicker: "ANTI-FRODE INTEGRATO",
    desc: "Ogni sede ha il proprio QR. La timbratura è validata via geo-localizzazione con anti-frode integrato. Storico immutabile per ogni ispezione.",
    icon: QrCode, accent: "#3B82F6", aiGenerated: false,
    video: null,
    poster: "/services/presenze-qr-gps-hero.jpg",
    bullets: ["Raggio 50–500 m", "Foto opzionale", "Storico immutabile"],
  },
];

const STORIE = [
  { id: 1, name: "Marco R.", role: "HR Director · Milano",
    img: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=600&q=80&auto=format&fit=crop",
    quote: "Abbiamo dimezzato il tempo per chiudere le presenze. Ora il martedì non è più un incubo." },
  { id: 2, name: "Giulia P.", role: "CFO · Torino",
    img: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=600&q=80&auto=format&fit=crop",
    quote: "Il modulo rimborsi paga da solo l'abbonamento. Punto." },
  { id: 3, name: "Davide C.", role: "Founder · Bologna",
    img: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=600&q=80&auto=format&fit=crop",
    quote: "Setup in 9 minuti. I miei 18 dipendenti l'hanno adottato senza una sola domanda." },
  { id: 4, name: "Sara M.", role: "People Ops · Roma",
    img: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=600&q=80&auto=format&fit=crop",
    quote: "Finalmente un HR software che le persone usano davvero, anche dal telefono." },
  { id: 5, name: "Luca B.", role: "COO · Padova",
    img: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=600&q=80&auto=format&fit=crop",
    quote: "Conformità GDPR fuori dalla scatola. Il nostro DPO ha sorriso, cosa rara." },
  { id: 6, name: "Chiara V.", role: "HR Manager · Firenze",
    img: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=600&q=80&auto=format&fit=crop",
    quote: "Il calendario AI suggerisce cose a cui non avevo pensato. Davvero." },
];

const TIERS = [
  { id: "tier10", name: "Tier 10", desc: "Piccole realtà", seats: "fino a 10 dipendenti",
    monthly: 20, setup: 199, perEmp: 1,
    features: ["Calendario intelligente", "QR + Geolocalizzazione", "Cedolini & ferie", "Export paghe CSV", "Supporto email"] },
  { id: "tier20", name: "Tier 20", desc: "Team in crescita", seats: "fino a 20 dipendenti",
    monthly: 40, setup: 199, perEmp: 1,
    features: ["Tutto Tier 10 +", "Rimborsi spese", "Firma digitale base", "Multi-utente admin", "Supporto prioritario"] },
  { id: "tier30", name: "Tier 30", desc: "Il più scelto dalle PMI", seats: "fino a 30 dipendenti", highlight: true,
    monthly: 60, setup: 299, perEmp: 1,
    features: ["Tutto Tier 20 +", "AI analisi presenze", "Trasferte GPS", "Turni & Timesheet", "Anomalie automatiche"] },
  { id: "tier50", name: "Tier 50", desc: "Aziende strutturate", seats: "fino a 50 dipendenti",
    monthly: 100, setup: 399, perEmp: 1,
    features: ["Tutto Tier 30 +", "Multi-sede illimitate", "Report avanzati", "Onboarding dedicato", "Customer Success"] },
];

/* =========================================================
   NAV — light, sticky
   ========================================================= */
function Nav({ onDemo, theme, onThemeToggle }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  // Close menu when route changes / link clicked
  useEffect(() => {
    if (menuOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);
  return (
    <nav
      className="fixed top-0 inset-x-0 z-50 transition-all duration-500"
      style={{
        background: scrolled ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0)",
        backdropFilter: scrolled ? "blur(20px) saturate(180%)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(20px) saturate(180%)" : "none",
        borderBottom: scrolled ? "1px solid rgba(15,23,42,0.06)" : "1px solid transparent",
      }}
      data-testid="landing-nav"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="brand-home-link" aria-label="Verixae home">
          <VerixaeLockup height={32} variant={scrolled ? "dark" : "light"} />
        </Link>
        <div className="hidden md:flex items-center gap-9 text-[13.5px]" style={{ color: "#475569", fontWeight: 500 }}>
          <a href="#servizi-detail" className="hover:text-[#0F172A] transition-colors">Servizi</a>
          <a href="#funzioni" className="hover:text-[#0F172A] transition-colors">Funzioni</a>
          <a href="#storie" className="hover:text-[#0F172A] transition-colors">Storie</a>
          <a href="#pricing" className="hover:text-[#0F172A] transition-colors">Prezzi</a>
          <a href="#faq" className="hover:text-[#0F172A] transition-colors">FAQ</a>
          <button onClick={onDemo} data-testid="nav-demo-btn" className="hover:text-[#0F172A] transition-colors">Demo</button>
        </div>
        {/* DESKTOP actions */}
        <div className="hidden md:flex items-center gap-2">
          <button
            type="button"
            onClick={onThemeToggle}
            data-testid="nav-theme-toggle"
            aria-label={theme === "night" ? "Passa al tema chiaro" : "Passa al tema scuro"}
            title={theme === "night" ? "Tema chiaro" : "Tema scuro"}
            className="w-9 h-9 rounded-full inline-flex items-center justify-center transition-all"
            style={{
              background: scrolled
                ? (theme === "night" ? "rgba(255,255,255,0.10)" : "#F1F5F9")
                : "rgba(255,255,255,0.10)",
              border: scrolled
                ? (theme === "night" ? "1px solid rgba(255,255,255,0.18)" : "1px solid rgba(15,23,42,0.08)")
                : "1px solid rgba(255,255,255,0.20)",
              color: scrolled
                ? (theme === "night" ? "#FACC15" : "#0F172A")
                : "#FFFFFF",
              backdropFilter: scrolled ? "none" : "blur(10px)",
            }}
          >
            {theme === "night"
              ? <Sun className="w-4 h-4" strokeWidth={2.2} />
              : <Moon className="w-4 h-4" strokeWidth={2.2} />}
          </button>
          <LanguageSwitcher variant={scrolled && theme === "day" ? "dark" : "light"} compact />
          <Link to="/login">
            <Button variant="ghost" data-testid="nav-login-btn"
              className="rounded-full text-sm px-4 h-9"
              style={{ color: scrolled && theme === "day" ? "#0F172A" : "#FFFFFF", background: "transparent" }}>
              Accedi
            </Button>
          </Link>
          <Link to="/register">
            <Button
              data-testid="nav-register-btn"
              className="rounded-full h-9 px-5 text-sm font-semibold border-0 text-white"
              style={{ background: "linear-gradient(135deg, #3B82F6 0%, #7C3AED 100%)" }}
            >
              Inizia gratis
            </Button>
          </Link>
        </div>
        {/* MOBILE hamburger */}
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          data-testid="nav-mobile-hamburger"
          aria-label="Apri menu"
          className="md:hidden w-10 h-10 rounded-full inline-flex items-center justify-center transition-all"
          style={{
            background: scrolled ? "#F1F5F9" : "rgba(255,255,255,0.12)",
            border: scrolled ? "1px solid rgba(15,23,42,0.08)" : "1px solid rgba(255,255,255,0.22)",
            color: scrolled ? "#0F172A" : "#FFFFFF",
            backdropFilter: scrolled ? "none" : "blur(10px)",
          }}
        >
          <Menu className="w-5 h-5" strokeWidth={2.2} />
        </button>
      </div>

      {/* MOBILE drawer */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-[60]" data-testid="nav-mobile-drawer">
          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            aria-label="Chiudi menu"
            data-testid="nav-mobile-overlay"
          />
          <aside
            className="absolute right-0 top-0 bottom-0 w-[88%] max-w-[360px] flex flex-col"
            style={{
              background: "linear-gradient(180deg, #0F172A 0%, #1E1B4B 100%)",
              boxShadow: "-20px 0 60px -10px rgba(0,0,0,0.40)",
            }}
          >
            <div className="flex items-center justify-between px-5 h-16 border-b border-white/10">
              <VerixaeLockup height={28} variant="light" />
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                data-testid="nav-mobile-close"
                aria-label="Chiudi menu"
                className="w-10 h-10 rounded-full inline-flex items-center justify-center"
                style={{ background: "rgba(255,255,255,0.10)", color: "#FFFFFF", border: "1px solid rgba(255,255,255,0.20)" }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-6">
              <div className="text-[10.5px] tracking-[0.22em] uppercase font-bold mb-3" style={{ color: "rgba(255,255,255,0.40)" }}>Esplora</div>
              <nav className="flex flex-col space-y-1 mb-6">
                {[
                  { href: "#servizi-detail", label: "Servizi" },
                  { href: "#funzioni", label: "Funzioni" },
                  { href: "#storie", label: "Storie" },
                  { href: "#pricing", label: "Prezzi" },
                  { href: "#faq", label: "FAQ" },
                ].map(item => (
                  <a key={item.href} href={item.href} onClick={() => setMenuOpen(false)}
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                    className="px-4 py-3 rounded-xl text-white font-semibold text-[15px] hover:bg-white/8 transition-colors"
                    style={{ background: "rgba(255,255,255,0.04)" }}>
                    {item.label}
                  </a>
                ))}
                <button onClick={() => { onDemo(); setMenuOpen(false); }}
                  data-testid="mobile-nav-demo"
                  className="text-left px-4 py-3 rounded-xl text-white font-semibold text-[15px] hover:bg-white/8 transition-colors"
                  style={{ background: "rgba(255,255,255,0.04)" }}>
                  Demo gratuita
                </button>
              </nav>
              <div className="text-[10.5px] tracking-[0.22em] uppercase font-bold mb-3" style={{ color: "rgba(255,255,255,0.40)" }}>Preferenze</div>
              <div className="flex items-center gap-2 mb-6">
                <button
                  type="button"
                  onClick={onThemeToggle}
                  data-testid="mobile-nav-theme"
                  className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-xl text-sm font-semibold text-white"
                  style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.10)" }}
                >
                  {theme === "night" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                  {theme === "night" ? "Chiaro" : "Scuro"}
                </button>
                <div className="flex-1 flex items-center justify-center h-11 rounded-xl"
                  style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.10)" }}>
                  <LanguageSwitcher variant="light" />
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-white/10 space-y-2">
              <Link to="/login" onClick={() => setMenuOpen(false)}>
                <Button variant="ghost" data-testid="mobile-nav-login"
                  className="w-full h-12 rounded-full text-white font-semibold"
                  style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.18)" }}>
                  Accedi
                </Button>
              </Link>
              <Link to="/register" onClick={() => setMenuOpen(false)}>
                <Button data-testid="mobile-nav-register"
                  className="w-full h-12 rounded-full font-semibold border-0 text-white"
                  style={{ background: "linear-gradient(135deg, #3B82F6 0%, #7C3AED 100%)" }}>
                  Inizia gratis
                </Button>
              </Link>
            </div>
          </aside>
        </div>
      )}
    </nav>
  );
}

/* =========================================================
   HERO + UNIFIED SERVICE CAROUSEL
   ========================================================= */
/* =========================================================
   HERO — FULL-SCREEN VIDEO BACKGROUND CAROUSEL
   Style: Lamborghini-like immersive cinematic hero.
   Videos cycle automatically; manual pills to switch.
   ========================================================= */
function Hero({ onDemo, onTour }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const videoRefs = useRef([]);

  // Play only the active video (no auto-rotate — user controls navigation)
  useEffect(() => {
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      if (i === activeIdx) {
        try { v.currentTime = 0; } catch {}
        v.play().catch(() => {});
      } else {
        v.pause();
      }
    });
  }, [activeIdx]);

  const active = SERVICES[activeIdx];

  return (
    <section
      className="relative w-full overflow-hidden"
      style={{ minHeight: 560, height: "100vh", background: "#05070F" }}
      data-testid="hero"
    >
      {/* Video carousel layers */}
      <div className="absolute inset-0">
        {/* Per-service color gradient — always renders even if video/poster fail to load on mobile */}
        {SERVICES.map((s, i) => (
          <div
            key={`bg-${s.id}`}
            className="absolute inset-0 transition-opacity duration-1000 ease-out"
            style={{
              opacity: activeIdx === i ? 1 : 0,
              background: `linear-gradient(135deg, #05070F 0%, #0F172A 35%, ${s.accent}44 75%, ${s.accent}66 100%)`,
            }}
          />
        ))}
        {/* Poster image layer (loads fast, no autoplay restrictions on mobile) */}
        {SERVICES.map((s, i) => (
          <img
            key={`poster-${s.id}`}
            src={s.poster}
            alt=""
            loading={i === 0 ? "eager" : "lazy"}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-out"
            style={{ opacity: activeIdx === i ? 1 : 0, objectPosition: "center 35%" }}
          />
        ))}
        {/* Video layer (skipped if no video URL configured) */}
        {SERVICES.map((s, i) => s.video ? (
          <video
            key={s.id}
            ref={(el) => (videoRefs.current[i] = el)}
            muted loop playsInline preload="metadata"
            poster={s.poster}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-out"
            style={{
              opacity: activeIdx === i ? 1 : 0,
              objectPosition: "center 35%",
            }}
          >
            <source src={s.video} type="video/mp4" />
          </video>
        ) : null)}

        {/* Dark gradient overlay for text readability */}
        <div className="absolute inset-0" style={{
          background: "linear-gradient(180deg, rgba(5,7,15,0.55) 0%, rgba(5,7,15,0.30) 35%, rgba(5,7,15,0.55) 70%, rgba(5,7,15,0.92) 100%)"
        }} />
        {/* Subtle vignette */}
        <div className="absolute inset-0" style={{
          background: "radial-gradient(ellipse at center, transparent 40%, rgba(5,7,15,0.45) 100%)"
        }} />
      </div>

      {/* Content overlay */}
      <div className="relative h-full flex flex-col" style={{ paddingTop: 64 }}>
        <div className="flex-1 max-w-[1400px] w-full mx-auto px-5 md:px-10 flex flex-col justify-center">
          {/* Kicker — service name */}
          <div className="flex items-center gap-3 mb-6 md:mb-8">
            <span className="w-12 h-px" style={{ background: "linear-gradient(90deg, #3B82F6 0%, #7C3AED 100%)" }} />
            <span
              key={active.id}
              className="reveal text-[11px] md:text-[12px] font-mono font-semibold tracking-[0.28em] uppercase text-white/90"
            >
              {active.kicker}
            </span>
          </div>

          {/* Massive headline */}
          <h1
            style={{
              fontFamily: "Inter, system-ui, sans-serif",
              fontWeight: 800,
              color: "#FFFFFF",
              letterSpacing: "-0.04em",
              lineHeight: 0.95,
              textShadow: "0 4px 32px rgba(0,0,0,0.4)",
            }}
            className="text-[28px] xs:text-[34px] sm:text-[48px] md:text-[64px] lg:text-[80px] xl:text-[96px] max-w-[1100px]"
          >
            Nuova commessa?
            <br />
            <span style={{
              background: "linear-gradient(135deg, #60A5FA 0%, #C084FC 60%, #F0ABFC 100%)",
              WebkitBackgroundClip: "text", backgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}>
              L'AI ti dice chi
            </span>
            <br />
            è il profilo giusto.
          </h1>

          <p className="mt-6 md:mt-8 max-w-[640px] text-white/85 text-[15px] md:text-[17px]" style={{ lineHeight: 1.5, textShadow: "0 2px 14px rgba(0,0,0,0.4)" }}>
            I CV dei tuoi dipendenti e candidati sono già nella libreria. Incolli una <strong>nuova commessa</strong> e l'AI fa l'<strong>analisi di associamento</strong>: punteggio di congruenza, punti di forza e lacune per ogni profilo. Risposta in 3 secondi, in italiano. Più un gestionale completo per la tua impresa.
          </p>

          <div className="mt-6 md:mt-10 flex flex-col xs:flex-row flex-wrap items-stretch xs:items-center gap-3 md:gap-4 w-full max-w-[640px]">
            {active.deepLink ? (
              <Link to={active.deepLink} className="flex-1 sm:flex-initial min-w-[200px]">
                <Button
                  data-testid="hero-cta-deeplink"
                  className="w-full rounded-full text-[13px] md:text-[14.5px] font-semibold border-0 text-white group whitespace-nowrap"
                  style={{ background: "linear-gradient(135deg, #10B981 0%, #0EA5E9 100%)", boxShadow: "0 16px 40px -10px rgba(16,185,129,0.55)", height: 50, paddingLeft: 22, paddingRight: 22 }}
                >
                  Prova il match ora
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            ) : (
              <Link to="/register" className="flex-1 sm:flex-initial min-w-[180px]">
                <Button
                  data-testid="hero-cta-primary"
                  className="w-full rounded-full text-[13px] md:text-[14.5px] font-semibold border-0 text-white group whitespace-nowrap"
                  style={{ background: "linear-gradient(135deg, #3B82F6 0%, #7C3AED 100%)", boxShadow: "0 16px 40px -10px rgba(124,58,237,0.55)", height: 50, paddingLeft: 22, paddingRight: 22 }}
                >
                  Prova gratis 14 giorni
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            )}
            <button
              onClick={onTour}
              data-testid="hero-cta-demo"
              className="flex-1 sm:flex-initial min-w-[140px] text-[13px] md:text-[14.5px] font-medium inline-flex items-center justify-center gap-2 px-5 rounded-full transition-all whitespace-nowrap"
              style={{ color: "#FFFFFF", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.20)", height: 50, backdropFilter: "blur(10px)" }}
            >
              <Play className="w-3.5 h-3.5" /> Prova in 60 secondi
            </button>
          </div>
        </div>

        {/* Service pills bottom — horizontally scrollable on mobile */}
        <div className="pb-5 md:pb-10">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10">
            <div className="flex items-center gap-2 md:gap-3 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1" data-testid="hero-service-pills">
              {SERVICES.map((s, i) => {
                const isActive = i === activeIdx;
                const Icon = s.icon;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => { setActiveIdx(i); }}
                    data-testid={`hero-pill-${s.id}`}
                    className="group relative inline-flex items-center gap-2 px-3 md:px-4 h-10 md:h-11 rounded-full transition-all duration-500 shrink-0"
                    style={{
                      background: isActive ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.08)",
                      border: isActive ? "1px solid rgba(255,255,255,0.95)" : "1px solid rgba(255,255,255,0.18)",
                      color: isActive ? "#0F172A" : "#FFFFFF",
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <Icon className="w-3.5 h-3.5" strokeWidth={2} />
                    <span className="text-[12px] md:text-[12.5px] font-semibold whitespace-nowrap">{s.title}</span>
                    {s.isNew && (
                      <span className="ml-0.5 inline-flex items-center text-[8.5px] font-bold tracking-widest rounded-full px-1.5 py-0.5"
                        style={{
                          background: isActive ? "linear-gradient(135deg,#10B981,#0EA5E9)" : "rgba(16,185,129,0.9)",
                          color: "#FFFFFF",
                        }}>
                        NEW
                      </span>
                    )}
                    {isActive && (
                      <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full" style={{ background: "linear-gradient(135deg, #3B82F6 0%, #7C3AED 100%)" }} />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Static accent bar (no auto-advance) */}
            <div className="mt-4 md:mt-5 h-[2px] w-full max-w-[180px] rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.15)" }}>
              <div className="h-full" style={{ width: "100%", background: "linear-gradient(90deg, #3B82F6 0%, #7C3AED 100%)" }} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   TRUST STRIP — logos + reassurances (light, under hero)
   ========================================================= */
function TrustStrip() {
  return (
    <section className="relative py-14 md:py-16" data-testid="trust-strip" style={{ background: "#FFFFFF", borderBottom: "1px solid rgba(15,23,42,0.06)" }}>
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="text-center text-[11px] tracking-[0.24em] uppercase font-semibold mb-6" style={{ color: "#94A3B8" }}>
          Scelta da oltre 1.200 team in Italia
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-10 md:gap-x-14 gap-y-5 mb-8" style={{ opacity: 0.6 }}>
          {["ATELIER 22", "NORDIC SPA", "STUDIO FORME", "BOTTEGA DIGITALE", "TESSILE VENETO", "BORGHI LIVING"].map((n) => (
            <span key={n} style={{ color: "#475569", fontWeight: 700, fontSize: 14, letterSpacing: "0.05em" }}>{n}</span>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-7 gap-y-2 text-[12.5px]" style={{ color: "#64748B" }}>
          <span className="inline-flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-emerald-500" strokeWidth={2.5} /> GDPR · Server EU</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-emerald-500" strokeWidth={2.5} /> Setup in 24 ore</span>
          <span className="inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-emerald-500" strokeWidth={2.5} /> AI in italiano</span>
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   SERVICES EXPLAINER — Left list + Right BIG carousel showcase
   Click a service in the list to swap the big card on the right.
   Auto-rotates every 6s. Responsive: stacks on mobile with
   horizontal scrollable tabs on top.
   ========================================================= */

/* Live broadcast counter — animated demo overlay for "Invio massivo" service */
function BroadcastLiveDemo({ active }) {
  const [sent, setSent] = useState(0);
  const target = 20000;

  useEffect(() => {
    if (!active) { setSent(0); return; }
    let raf;
    const start = Date.now();
    const duration = 4500;
    const tick = () => {
      const elapsed = Date.now() - start;
      const p = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
      setSent(Math.floor(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active]);

  const pct = (sent / target) * 100;
  const channels = [
    { name: "Push",     icon: Bell,          ratio: 0.45, color: "#3B82F6" },
    { name: "Email",    icon: Mail,          ratio: 0.30, color: "#A855F7" },
    { name: "SMS",      icon: Smartphone,    ratio: 0.15, color: "#06B6D4" },
    { name: "WhatsApp", icon: MessageCircle, ratio: 0.10, color: "#10B981" },
  ];

  return (
    <div
      className="absolute bottom-4 right-4 w-[280px] md:w-[320px] rounded-2xl overflow-hidden p-4 reveal"
      style={{
        background: "rgba(5,7,15,0.78)",
        backdropFilter: "blur(20px) saturate(180%)",
        WebkitBackdropFilter: "blur(20px) saturate(180%)",
        border: "1px solid rgba(255,255,255,0.12)",
        boxShadow: "0 20px 60px -20px rgba(220,38,38,0.5), 0 0 0 1px rgba(220,38,38,0.20) inset",
      }}
      data-testid="broadcast-live-demo"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="inline-flex items-center gap-1.5">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: "#DC2626" }} />
            <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: "#DC2626" }} />
          </span>
          <span className="text-[10.5px] font-mono font-bold tracking-[0.18em] uppercase" style={{ color: "#FCA5A5" }}>
            Live · in invio
          </span>
        </div>
        <div className="text-[10px] font-mono" style={{ color: "rgba(255,255,255,0.5)" }}>
          {Math.floor(pct)}%
        </div>
      </div>

      {/* Big counter */}
      <div className="flex items-baseline gap-1.5 mb-3">
        <span
          className="font-mono tabular-nums text-white"
          style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 28, letterSpacing: "-0.02em", lineHeight: 1 }}
        >
          {sent.toLocaleString("it-IT")}
        </span>
        <span className="text-[12px]" style={{ color: "rgba(255,255,255,0.55)" }}>
          / {target.toLocaleString("it-IT")} dipendenti
        </span>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 rounded-full overflow-hidden mb-3.5" style={{ background: "rgba(255,255,255,0.10)" }}>
        <div
          className="h-full transition-all rounded-full"
          style={{ width: `${pct}%`, background: "linear-gradient(90deg, #DC2626 0%, #F59E0B 50%, #EAB308 100%)" }}
        />
      </div>

      {/* Channel breakdown */}
      <div className="grid grid-cols-2 gap-1.5">
        {channels.map((c) => {
          const count = Math.floor(sent * c.ratio);
          const Icon = c.icon;
          return (
            <div
              key={c.name}
              className="rounded-lg px-2 py-1.5 flex items-center gap-2"
              style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
            >
              <Icon className="w-3 h-3 shrink-0" style={{ color: c.color }} />
              <div className="min-w-0 flex-1">
                <div className="text-[9.5px] font-mono uppercase tracking-[0.1em] leading-none" style={{ color: "rgba(255,255,255,0.55)" }}>
                  {c.name}
                </div>
                <div className="text-[11.5px] font-mono tabular-nums font-bold text-white mt-0.5 leading-none">
                  {count.toLocaleString("it-IT")}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* Live counter chip for Chat aziendale — shows online users */
function ChatLiveDemo({ active }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) { setCount(0); return; }
    let raf, target = 247;
    const start = Date.now();
    const duration = 2500;
    const tick = () => {
      const elapsed = Date.now() - start;
      const p = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setCount(Math.floor(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active]);

  return (
    <div
      className="absolute bottom-4 right-4 rounded-2xl overflow-hidden px-4 py-3 reveal"
      style={{
        background: "rgba(5,7,15,0.78)",
        backdropFilter: "blur(20px) saturate(180%)",
        WebkitBackdropFilter: "blur(20px) saturate(180%)",
        border: "1px solid rgba(255,255,255,0.12)",
        boxShadow: "0 16px 40px -16px rgba(236,72,153,0.4)",
      }}
      data-testid="chat-live-demo"
    >
      <div className="flex items-center gap-3">
        <div className="flex -space-x-2">
          {["#EC4899", "#3B82F6", "#10B981", "#F59E0B"].map((c, i) => (
            <div key={`avatar-${c}`} className="w-7 h-7 rounded-full border-2" style={{ background: c, borderColor: "#05070F" }} />
          ))}
        </div>
        <div>
          <div className="text-[10.5px] font-mono uppercase tracking-[0.18em] font-bold" style={{ color: "#F9A8D4" }}>
            Online ora
          </div>
          <div className="font-mono tabular-nums text-white font-bold text-[18px] leading-none mt-1">
            {count.toLocaleString("it-IT")}
          </div>
        </div>
      </div>
    </div>
  );
}

/* Live counter for Blockchain — shows blocks confirmed */
function BlockchainLiveDemo({ active }) {
  const [blocks, setBlocks] = useState(0);
  const [hashShift, setHashShift] = useState(0);
  useEffect(() => {
    if (!active) { setBlocks(0); setHashShift(0); return; }
    let raf, target = 18472, start = Date.now(), duration = 3500;
    const tick = () => {
      const elapsed = Date.now() - start;
      const p = Math.min(elapsed / duration, 1);
      setBlocks(Math.floor(target * (1 - Math.pow(1 - p, 3))));
      setHashShift((s) => s + 1);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active]);

  const hash = "0x" + Array.from({ length: 12 }).map((_, i) => "0123456789abcdef"[(i * 7 + hashShift) % 16]).join("");

  return (
    <div
      className="absolute bottom-4 right-4 w-[260px] rounded-2xl overflow-hidden px-4 py-3 reveal"
      style={{
        background: "rgba(5,7,15,0.78)",
        backdropFilter: "blur(20px) saturate(180%)",
        WebkitBackdropFilter: "blur(20px) saturate(180%)",
        border: "1px solid rgba(255,255,255,0.12)",
        boxShadow: "0 16px 40px -16px rgba(15,118,110,0.5)",
      }}
      data-testid="blockchain-live-demo"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="inline-flex items-center gap-1.5">
          <ShieldCheck className="w-3 h-3" style={{ color: "#5EEAD4" }} />
          <span className="text-[10.5px] font-mono font-bold tracking-[0.18em] uppercase" style={{ color: "#5EEAD4" }}>
            Hash confermati
          </span>
        </div>
        <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#10B981" }} />
      </div>
      <div className="font-mono tabular-nums text-white font-bold text-[22px] leading-none mb-2" style={{ letterSpacing: "-0.01em" }}>
        {blocks.toLocaleString("it-IT")}
      </div>
      <div className="text-[10px] font-mono px-2 py-1.5 rounded-md" style={{ background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.55)", border: "1px solid rgba(255,255,255,0.06)" }}>
        sha256: {hash}…
      </div>
    </div>
  );
}

function ServiceLiveDemo({ id, active }) {
  if (id === "broadcast")  return <BroadcastLiveDemo active={active} />;
  if (id === "chat")       return <ChatLiveDemo active={active} />;
  if (id === "blockchain") return <BlockchainLiveDemo active={active} />;
  if (id === "fedelta")    return <LoyaltyCardDemo active={active} />;
  if (id === "cral")       return <CralDemo active={active} />;
  return null;
}

/* Loyalty Card — animated wallet-pass for "Carta Fedeltà Punti" */
function LoyaltyCardDemo({ active }) {
  const [points, setPoints] = useState(0);
  useEffect(() => {
    if (!active) { setPoints(0); return; }
    let raf, target = 287, start = Date.now(), duration = 2200;
    const tick = () => {
      const elapsed = Date.now() - start;
      const p = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setPoints(Math.floor(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active]);

  return (
    <div
      className="absolute bottom-4 right-4 w-[280px] md:w-[300px] rounded-2xl overflow-hidden p-5 reveal loyalty-tilt"
      style={{
        background: "linear-gradient(135deg, #1F1B0F 0%, #3F2C0C 50%, #6B4D0E 100%)",
        border: "1px solid rgba(250, 204, 21, 0.40)",
        boxShadow: "0 30px 70px -20px rgba(234,179,8,0.5), 0 0 0 1px rgba(234,179,8,0.20) inset",
      }}
      data-testid="loyalty-card-demo"
    >
      {/* Holographic shimmer */}
      <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
        background: "linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.18) 50%, transparent 70%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 4s ease-in-out infinite",
      }} />

      {/* Header */}
      <div className="relative flex items-center justify-between mb-4">
        <div className="inline-flex items-center gap-1.5">
          <Award className="w-3 h-3" style={{ color: "#FACC15" }} />
          <span className="text-[10px] font-mono font-bold tracking-[0.20em] uppercase" style={{ color: "#FACC15" }}>
            Verixae Loyalty
          </span>
        </div>
        <span className="text-[9.5px] font-mono tracking-[0.12em] uppercase font-bold px-2 py-0.5 rounded-full"
              style={{ background: "rgba(250,204,21,0.18)", color: "#FDE68A", border: "1px solid rgba(250,204,21,0.35)" }}>
          GOLD
        </span>
      </div>

      {/* Member name */}
      <div className="relative">
        <div className="text-[9px] font-mono uppercase tracking-[0.20em] mb-0.5" style={{ color: "rgba(253,230,138,0.55)" }}>Dipendente</div>
        <div className="text-white font-bold text-[14px]" style={{ letterSpacing: "-0.01em" }}>MARCO ROSSI</div>
        <div className="text-[10.5px] font-mono mt-0.5" style={{ color: "rgba(253,230,138,0.65)" }}>· 4521 8849 ·</div>
      </div>

      {/* Points */}
      <div className="relative mt-3.5 pt-3 border-t" style={{ borderColor: "rgba(250,204,21,0.20)" }}>
        <div className="flex items-end justify-between">
          <div>
            <div className="text-[9px] font-mono uppercase tracking-[0.18em] mb-0.5" style={{ color: "rgba(253,230,138,0.55)" }}>Punti questo mese</div>
            <div className="flex items-baseline gap-1.5">
              <span className="font-mono tabular-nums text-white font-extrabold text-[28px] leading-none" style={{ letterSpacing: "-0.02em" }}>
                {points}
              </span>
              <span className="text-[12px] font-bold" style={{ color: "#FACC15" }}>pt</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[9px] font-mono uppercase tracking-[0.18em] mb-0.5" style={{ color: "rgba(253,230,138,0.55)" }}>Condotta</div>
            <div className="inline-flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} className="w-3 h-3" fill="#FACC15" stroke="#FACC15" />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Decorative chip */}
      <div className="absolute top-12 right-5 w-8 h-6 rounded-sm" style={{
        background: "linear-gradient(135deg, #FBBF24 0%, #D97706 100%)",
        boxShadow: "0 0 0 1px rgba(255,255,255,0.10) inset, 0 4px 8px -2px rgba(0,0,0,0.4)",
      }}>
        <div className="absolute inset-1 rounded-sm" style={{
          background: "repeating-linear-gradient(90deg, rgba(0,0,0,0.2) 0 1px, transparent 1px 3px), repeating-linear-gradient(0deg, rgba(0,0,0,0.2) 0 1px, transparent 1px 3px)",
        }} />
      </div>
    </div>
  );
}

/* CRAL — Welfare partners feed */
function CralDemo({ active }) {
  const [partners, setPartners] = useState(0);
  useEffect(() => {
    if (!active) { setPartners(0); return; }
    let raf, target = 512, start = Date.now(), duration = 2800;
    const tick = () => {
      const elapsed = Date.now() - start;
      const p = Math.min(elapsed / duration, 1);
      setPartners(Math.floor(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active]);

  const offers = [
    { name: "Decathlon",     pct: -15, color: "#0EA5E9" },
    { name: "Cinema",         pct: -30, color: "#A855F7" },
    { name: "Esselunga",      pct: -8,  color: "#10B981" },
  ];

  return (
    <div
      className="absolute bottom-4 right-4 w-[280px] rounded-2xl overflow-hidden p-4 reveal"
      style={{
        background: "rgba(5,7,15,0.78)",
        backdropFilter: "blur(20px) saturate(180%)",
        WebkitBackdropFilter: "blur(20px) saturate(180%)",
        border: "1px solid rgba(255,255,255,0.12)",
        boxShadow: "0 18px 50px -16px rgba(244,114,182,0.5)",
      }}
      data-testid="cral-demo"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="inline-flex items-center gap-1.5">
          <Gift className="w-3 h-3" style={{ color: "#F9A8D4" }} />
          <span className="text-[10px] font-mono font-bold tracking-[0.18em] uppercase" style={{ color: "#F9A8D4" }}>
            Partner attivi
          </span>
        </div>
        <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#F472B6" }} />
      </div>
      <div className="font-mono tabular-nums text-white font-extrabold text-[24px] leading-none mb-3" style={{ letterSpacing: "-0.02em" }}>
        {partners.toLocaleString("it-IT")}
      </div>
      <div className="space-y-1.5">
        {offers.map((o) => (
          <div key={o.name} className="flex items-center justify-between text-[11px] px-2 py-1.5 rounded-md" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: o.color }} />
              <span className="text-white font-semibold">{o.name}</span>
            </div>
            <span className="font-mono font-bold" style={{ color: "#34D399" }}>{o.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ServicesExplainer() {
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const videoRef = useRef(null);
  const ref = useRef(null);

  const s = SERVICES[active];
  const Icon = s.icon;

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !s.video) return;
    try { v.currentTime = 0; } catch {}
    v.play().catch(() => {});
  }, [active, s.video]);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <section id="servizi-detail" className="relative py-20 md:py-28" style={{ background: "#FFFFFF" }} data-testid="services-explainer">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        {/* Section header */}
        <div className="max-w-[800px] mb-8 md:mb-10">
          <div className="text-[11px] tracking-[0.24em] uppercase font-semibold mb-4" style={{ color: "#3B82F6" }}>— I nostri servizi</div>
          <h2
            style={{ fontFamily: "Inter, system-ui, sans-serif", fontWeight: 800, color: "#0F172A", letterSpacing: "-0.035em", lineHeight: 1.02 }}
            className="text-[32px] sm:text-[44px] md:text-[60px]"
          >
            Una piattaforma.
            <br />
            <span style={{ background: "linear-gradient(135deg, #3B82F6 0%, #7C3AED 100%)",
              WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              Quattordici strumenti
            </span> in un menù.
          </h2>
          <p className="mt-4 max-w-[600px] text-[15px] md:text-[16px]" style={{ color: "#475569", lineHeight: 1.55 }}>
            Apri il menù qui sotto e scegli lo strumento che vuoi esplorare. L'anteprima video mostra come funziona nella pratica.
          </p>
        </div>

        {/* DROPDOWN SELECTOR — primary navigation */}
        <div className="mb-8 md:mb-10 max-w-[560px]" ref={ref} data-testid="services-dropdown">
          <div className="text-[11px] tracking-[0.22em] uppercase font-semibold mb-3" style={{ color: "#7C3AED" }}>— Strumento selezionato</div>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid="services-dropdown-trigger"
            className="w-full rounded-2xl px-4 py-3.5 flex items-center justify-between gap-4 transition-all"
            style={{
              background: "#FFFFFF",
              border: "1px solid rgba(15,23,42,0.10)",
              boxShadow: open ? "0 14px 32px -10px rgba(15,23,42,0.20)" : "0 2px 6px -2px rgba(15,23,42,0.06)",
            }}
          >
            <span className="flex items-center gap-3 min-w-0">
              <span
                className="w-10 h-10 rounded-lg flex items-center justify-center text-white shrink-0"
                style={{ background: `linear-gradient(135deg, ${s.accent} 0%, ${s.accent}CC 100%)` }}
              >
                <Icon className="w-5 h-5" strokeWidth={2} />
              </span>
              <span className="text-left min-w-0">
                <span className="block text-[14px] font-bold truncate" style={{ color: "#0F172A", letterSpacing: "-0.015em" }}>{s.title}</span>
                <span className="block text-[10.5px] font-mono uppercase tracking-[0.12em] truncate" style={{ color: "#94A3B8" }}>{s.kicker}</span>
              </span>
            </span>
            <span className="flex items-center gap-2 shrink-0">
              <span className="hidden sm:inline text-[10.5px] font-mono uppercase tracking-[0.16em] font-bold" style={{ color: "#94A3B8" }}>
                {String(active + 1).padStart(2, "0")}/{String(SERVICES.length).padStart(2, "0")}
              </span>
              <ChevronRight className={`w-4 h-4 transition-transform duration-300 ${open ? "rotate-90" : "rotate-0"}`} style={{ color: "#64748B" }} />
            </span>
          </button>

          {open && (
            <div
              className="mt-2 w-full rounded-2xl overflow-hidden reveal"
              style={{
                background: "#FFFFFF",
                border: "1px solid rgba(15,23,42,0.10)",
                boxShadow: "0 24px 50px -15px rgba(15,23,42,0.22)",
                maxHeight: 420,
                overflowY: "auto",
              }}
              data-testid="services-dropdown-panel"
            >
              {SERVICES.map((sv, i) => {
                const isActive = i === active;
                const Ic = sv.icon;
                return (
                  <button
                    key={sv.id}
                    type="button"
                    onClick={() => { setActive(i); setOpen(false); }}
                    data-testid={`services-dropdown-item-${sv.id}`}
                    className="w-full px-4 py-3 flex items-center gap-3 text-left transition-colors hover:bg-[rgba(15,23,42,0.04)]"
                    style={{ background: isActive ? "rgba(59,130,246,0.06)" : "transparent" }}
                  >
                    <span
                      className="w-9 h-9 rounded-lg flex items-center justify-center text-white shrink-0"
                      style={{ background: `linear-gradient(135deg, ${sv.accent} 0%, ${sv.accent}CC 100%)` }}
                    >
                      <Ic className="w-4 h-4" strokeWidth={2} />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[13.5px] font-bold truncate" style={{ color: "#0F172A" }}>{sv.title}</span>
                      <span className="block text-[10.5px] font-mono uppercase tracking-[0.12em] truncate" style={{ color: "#94A3B8" }}>{sv.kicker}</span>
                    </span>
                    {isActive && (
                      <span className="text-[10px] font-bold tracking-[0.16em] uppercase px-2 py-0.5 rounded-full shrink-0"
                            style={{ background: "linear-gradient(135deg, #3B82F6 0%, #7C3AED 100%)", color: "#FFFFFF" }}>
                        Attivo
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* PREVIEW CARD — shows selected service */}
        <article
          key={s.id}
          className="rounded-3xl overflow-hidden relative reveal"
          style={{
            background: "#0F172A",
            border: "1px solid rgba(15,23,42,0.08)",
            boxShadow: "0 30px 80px -20px rgba(15,23,42,0.20)",
          }}
          data-testid={`services-big-${s.id}`}
        >
          {/* Big video */}
          <div className="relative w-full aspect-[16/9] md:aspect-[21/9] overflow-hidden" style={{ background: "#05070F" }}>
            <img src={s.poster} alt={s.title} className={`absolute inset-0 w-full h-full object-cover ${s.video ? "opacity-60" : "opacity-100"}`} />
            {s.video && (
              <video
                ref={videoRef}
                muted loop playsInline preload="metadata"
                poster={s.poster}
                className="absolute inset-0 w-full h-full object-cover"
              >
                <source src={s.video} type="video/mp4" />
              </video>
            )}
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,23,42,0.20) 0%, transparent 35%, rgba(15,23,42,0.85) 100%)" }} />

            {s.aiGenerated && (
              <div className="absolute top-5 left-5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10.5px] font-bold tracking-[0.1em] uppercase text-white backdrop-blur"
                   style={{ background: "linear-gradient(135deg, rgba(59,130,246,0.85) 0%, rgba(124,58,237,0.85) 100%)", boxShadow: "0 6px 18px -4px rgba(124,58,237,0.6)" }}>
                <Sparkles className="w-3 h-3" /> Sora AI
              </div>
            )}

            <div className="absolute top-5 right-5 px-3 py-1.5 rounded-full text-[10.5px] font-bold tracking-[0.18em] uppercase backdrop-blur"
                 style={{ background: "rgba(255,255,255,0.10)", color: "#FFFFFF", border: "1px solid rgba(255,255,255,0.20)" }}>
              {s.kicker}
            </div>

            <div className="absolute inset-x-0 bottom-0 p-6 md:p-10 flex items-end gap-5">
              <div
                className="w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center text-white shrink-0"
                style={{ background: `linear-gradient(135deg, ${s.accent} 0%, ${s.accent}CC 100%)`, boxShadow: `0 14px 32px -8px ${s.accent}AA` }}
              >
                <Icon className="w-7 h-7 md:w-9 md:h-9" strokeWidth={1.8} />
              </div>
              <h3 style={{ fontFamily: "Inter, system-ui, sans-serif", fontWeight: 800, color: "#FFFFFF", letterSpacing: "-0.035em", lineHeight: 1, textShadow: "0 2px 14px rgba(0,0,0,0.4)" }}
                  className="text-[32px] md:text-[56px] lg:text-[72px]">
                {s.title}
              </h3>
            </div>

            <ServiceLiveDemo id={s.id} active={true} />
          </div>

          {/* Body */}
          <div className="p-6 md:p-10 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10" style={{ background: "#0F172A" }}>
            <div className="md:col-span-7">
              <p className="text-[15px] md:text-[18px]" style={{ color: "rgba(255,255,255,0.85)", lineHeight: 1.6 }}>
                {s.desc}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link to="/register">
                  <Button
                    data-testid={`services-cta-${s.id}`}
                    className="rounded-full h-12 px-7 text-[14px] font-semibold border-0 text-white"
                    style={{ background: `linear-gradient(135deg, ${s.accent} 0%, ${s.accent}DD 100%)`, boxShadow: `0 14px 32px -8px ${s.accent}80` }}
                  >
                    Prova {s.title} <ArrowRight className="ml-2 w-3.5 h-3.5" />
                  </Button>
                </Link>
                <div className="inline-flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setActive((i) => (i - 1 + SERVICES.length) % SERVICES.length)}
                    data-testid="services-prev"
                    className="w-10 h-10 rounded-full inline-flex items-center justify-center transition-all"
                    style={{ background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.18)", color: "#FFFFFF" }}
                    aria-label="Strumento precedente"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setActive((i) => (i + 1) % SERVICES.length)}
                    data-testid="services-next"
                    className="w-10 h-10 rounded-full inline-flex items-center justify-center transition-all"
                    style={{ background: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.20)", color: "#FFFFFF" }}
                    aria-label="Strumento successivo"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="text-[10.5px] tracking-[0.22em] uppercase font-semibold mb-3" style={{ color: "rgba(255,255,255,0.4)" }}>Caratteristiche chiave</div>
              <ul className="space-y-3">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-[14px] md:text-[15px]" style={{ color: "rgba(255,255,255,0.92)" }}>
                    <span className="mt-0.5 w-4 h-4 rounded-full flex items-center justify-center shrink-0"
                          style={{ background: `linear-gradient(135deg, ${s.accent} 0%, ${s.accent}CC 100%)` }}>
                      <Check className="w-2.5 h-2.5 text-white" strokeWidth={3.5} />
                    </span>
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}

/* =========================================================
   FUNCTIONS GRID — detailed functionalities, after services
   ========================================================= */
const FUNCTIONS = [
  { icon: Shield,       title: "Anti-frode GPS",       desc: "Validazione perimetro configurabile per ogni timbratura, con foto opzionale e log immutabile.", img: "https://images.unsplash.com/photo-1604079628040-94301bb21b91?w=1600&q=80&auto=format&fit=crop" },
  { icon: Sparkles,     title: "OCR italiano",         desc: "Estrazione automatica dei dati da scontrini, fatture e ricevute in lingua italiana.", img: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1600&q=80&auto=format&fit=crop" },
  { icon: CalendarRange,title: "AI Anomalie",          desc: "Rileva pattern sospetti nelle presenze (timbrature gemelle, fuori orario, fuori area).", img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1600&q=80&auto=format&fit=crop" },
  { icon: Wallet,       title: "Export paghe",         desc: "Tracciati pronti per Zucchetti, TeamSystem, Inaz, Inail, completi di causali.", img: "https://images.unsplash.com/photo-1554224154-26032cdc0b40?w=1600&q=80&auto=format&fit=crop" },
  { icon: FileSignature,title: "Firma eIDAS",          desc: "Hash SHA-256 + audit log GDPR. Conforme al regolamento europeo, valore legale.", img: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1600&q=80&auto=format&fit=crop" },
  { icon: QrCode,       title: "Multi-sede",           desc: "Sedi illimitate con orari, festività e raggi GPS configurabili in autonomia.", img: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1600&q=80&auto=format&fit=crop" },
  { icon: Receipt,      title: "Audit Log GDPR",       desc: "Tracciabilità completa di ogni operazione amministrativa, esportabile in JSON o CSV.", img: "https://images.unsplash.com/photo-1551288049-b1f3a3a52bf6?w=1600&q=80&auto=format&fit=crop" },
  { icon: Play,         title: "PWA installabile",     desc: "Funziona su iOS e Android come app nativa, anche offline. Push notification incluse.", img: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1600&q=80&auto=format&fit=crop" },
  { icon: Clock,        title: "Sync Calendar",        desc: "Google Calendar, Outlook, Apple Calendar, iCal: integrazione bidirezionale in tempo reale.", img: "https://images.unsplash.com/photo-1606857521015-7f9fcf423740?w=1600&q=80&auto=format&fit=crop" },
  /* --- v12: 6 new innovative functions --- */
  { icon: Star,         title: "Gamification HR",      desc: "Achievement automatici, leaderboard di team, badge per traguardi. Aumenta retention del 38%.", img: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1600&q=80&auto=format&fit=crop" },
  { icon: Trophy,        title: "Bonus puntualità",     desc: "Premi automatici basati su KPI: presenza, puntualità, completamento mansioni. Configurabile per ruolo.", img: "https://images.unsplash.com/photo-1567427017947-545c5f8d16ad?w=1600&q=80&auto=format&fit=crop" },
  { icon: Brain,         title: "AI Performance Review", desc: "GPT-5 propone domande 1:1 personalizzate per dipendente, sintetizza il feedback in report leggibile.", img: "https://images.unsplash.com/photo-1488998427799-e3362cec87c3?w=1600&q=80&auto=format&fit=crop" },
  { icon: Heart,         title: "Wellbeing Pulse",     desc: "Sondaggi anonimi settimanali sul benessere. Heatmap del clima aziendale, alert su rischio burnout.", img: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1600&q=80&auto=format&fit=crop" },
  { icon: GraduationCap, title: "Formazione obbligatoria", desc: "Tracking automatico di corsi sicurezza, anti-corruzione, GDPR. Scadenze, attestati, esportazione INAIL.", img: "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=1600&q=80&auto=format&fit=crop" },
  { icon: Globe,         title: "Multi-lingua",         desc: "Interfaccia in italiano, inglese, francese, spagnolo, rumeno, arabo. Per team con dipendenti internazionali.", img: "https://images.unsplash.com/photo-1526666923127-b2970f64b422?w=1600&q=80&auto=format&fit=crop" },
  { icon: ScanLine,      title: "Riconoscimento facciale", desc: "Timbratura biometrica opzionale: foto + face matching anti-spoofing. Conforme GDPR.", img: "https://images.unsplash.com/photo-1567446537708-ac4aa75c9c28?w=1600&q=80&auto=format&fit=crop" },
  /* --- v13: 9 more innovative functions (full a-l list) --- */
  { icon: UserPlus,      title: "Onboarding AI",        desc: "Il nuovo dipendente firma online, l'AI genera contratto, prenota visita medica e formazione obbligatoria. In 30 secondi.", img: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1600&q=80&auto=format&fit=crop" },
  { icon: Layers,        title: "Smart Scheduling",     desc: "L'AI genera turni ottimali considerando preferenze, ferie, costi e normativa CCNL. Costo orario in tempo reale.", img: "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=1600&q=80&auto=format&fit=crop" },
  { icon: Lock,          title: "Whistleblowing GDPR",  desc: "Canale anonimo per segnalazioni. Crittografato end-to-end. Obbligatorio per aziende >50 dipendenti dal D.lgs 24/2023.", img: "https://images.unsplash.com/photo-1614064641938-3bbee52942c7?w=1600&q=80&auto=format&fit=crop" },
  { icon: CreditCard,    title: "Pagamenti Stripe",     desc: "Cedolino + bonifico automatico al dipendente. Rimborsi spese istantanei. Costo Stripe 0,25%.", img: "https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?w=1600&q=80&auto=format&fit=crop" },
  { icon: Bell,          title: "Notifiche Multi-canale", desc: "Push + email + WhatsApp + Telegram. Il dipendente sceglie il canale. Approvazioni inline da chat.", img: "https://images.unsplash.com/photo-1611606063065-ee7946f0787a?w=1600&q=80&auto=format&fit=crop" },
  { icon: FolderLock,    title: "Document Vault",       desc: "Tracking scadenze contratti, visite mediche, formazione, patentini. Notifica 30 giorni prima. Rinnovo guidato.", img: "https://images.unsplash.com/photo-1568667256549-094345857637?w=1600&q=80&auto=format&fit=crop" },
  { icon: Plane,         title: "Trasferte AI Booking", desc: "Prenotazione integrata treno/aereo/hotel via TrenItalia + Booking. Auto-rimborso. Policy aziendali rispettate.", img: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1600&q=80&auto=format&fit=crop" },
  { icon: Home,          title: "Smart Working Tracker", desc: "Autocertificazione remote work, timbratura geo-libera ma loggata. Report mensile. Conforme L.81/2017.", img: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=1600&q=80&auto=format&fit=crop" },
  { icon: Bot,           title: "AI Chatbot HR",        desc: "Il dipendente chiede 'Quante ferie ho?' via chat, l'AI risponde in tempo reale. Apre ticket automatici per HR.", img: "https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=1600&q=80&auto=format&fit=crop" },
];

function FunctionsCarousel() {
  const [active, setActive] = useState(0);
  const stripRef = useRef(null);

  const palette = [
    "#3B82F6", "#7C3AED", "#06B6D4", "#EC4899", "#10B981",
    "#F59E0B", "#6366F1", "#EAB308", "#F472B6", "#0EA5E9",
    "#A855F7", "#DC2626", "#0F766E", "#14B8A6", "#22C55E",
    "#84CC16", "#FB923C", "#EF4444", "#8B5CF6", "#06B6D4",
    "#10B981", "#F472B6", "#3B82F6", "#7C3AED", "#06B6D4",
  ];

  // Keep thumb strip in sync with active (no auto-advance — user controls navigation)
  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    const child = el.children?.[active];
    if (child) {
      child.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [active]);

  const f = FUNCTIONS[active];
  const Icon = f.icon;
  const color = palette[active % palette.length];

  const go = (dir) => {
    setActive((i) => (i + dir + FUNCTIONS.length) % FUNCTIONS.length);
  };

  return (
    <section id="funzioni" className="relative py-20 md:py-28" style={{ background: "#FAFAF7" }} data-testid="functions">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-8 md:mb-10">
          <div className="max-w-[700px]">
            <div className="text-[11px] tracking-[0.24em] uppercase font-semibold mb-4" style={{ color: "#7C3AED" }}>— Funzioni nel dettaglio</div>
            <h2
              style={{ fontFamily: "Inter, system-ui, sans-serif", fontWeight: 800, color: "#0F172A", letterSpacing: "-0.035em", lineHeight: 1.02 }}
              className="text-[32px] sm:text-[44px] md:text-[60px]"
            >
              Venticinque funzioni che
              <br />
              <span style={{ background: "linear-gradient(135deg, #3B82F6 0%, #7C3AED 100%)",
                WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                fanno la differenza
              </span>.
            </h2>
            <p className="mt-4 max-w-[500px] text-[15px] md:text-[16px]" style={{ color: "#475569", lineHeight: 1.55 }}>
              Un carosello cinematico: una funzione alla volta, in piena luce. Naviga con le frecce o tocca le miniature.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => go(-1)}
              data-testid="functions-prev"
              className="w-11 h-11 rounded-full flex items-center justify-center transition-all"
              style={{ background: "#FFFFFF", border: "1px solid rgba(15,23,42,0.10)", color: "#0F172A", boxShadow: "0 2px 6px -2px rgba(15,23,42,0.08)" }}
              aria-label="Funzione precedente"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              data-testid="functions-next"
              className="w-11 h-11 rounded-full flex items-center justify-center transition-all"
              style={{ background: "#0F172A", color: "#FFFFFF", boxShadow: "0 8px 24px -6px rgba(15,23,42,0.30)" }}
              aria-label="Funzione successiva"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* SPOTLIGHT — single function shown cinematically */}
        <article
          key={f.title}
          className="relative rounded-3xl overflow-hidden reveal"
          style={{
            background: `linear-gradient(135deg, #0A0E27 0%, #0F172A 30%, ${color}22 75%, ${color}55 100%)`,
            border: "1px solid rgba(15,23,42,0.08)",
            boxShadow: "0 30px 80px -20px rgba(15,23,42,0.25)",
            minHeight: 460,
          }}
          data-testid={`function-spotlight-${active}`}
        >
          {/* Layered color overlays — works without network */}
          <div className="absolute inset-0" style={{
            background: `radial-gradient(circle at 80% 30%, ${color}55 0%, transparent 55%)`,
          }} />
          <div className="absolute inset-0" style={{
            background: `radial-gradient(circle at 90% 90%, ${color}33 0%, transparent 45%)`,
          }} />
          {/* Giant decorative icon (bottom-right) */}
          <div className="absolute pointer-events-none select-none" style={{
            right: "-40px", bottom: "-60px", opacity: 0.10, color: color,
          }}>
            <Icon strokeWidth={1} style={{ width: 360, height: 360 }} />
          </div>
          {/* Subtle grain overlay for depth */}
          <div className="absolute inset-0 mix-blend-overlay opacity-30" style={{
            backgroundImage: "radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)",
            backgroundSize: "3px 3px",
          }} />

          {/* Number badge */}
          <div className="absolute top-6 right-6 text-[10.5px] font-mono tracking-[0.20em] uppercase font-bold px-3 py-1.5 rounded-full backdrop-blur"
                style={{ background: `${color}22`, color: "#FFFFFF", border: `1px solid ${color}55` }}>
            {String(active + 1).padStart(2, "0")} / {String(FUNCTIONS.length).padStart(2, "0")}
          </div>

          {/* Content */}
          <div className="relative p-7 md:p-12 lg:p-16 flex flex-col h-full min-h-[460px] max-w-[700px]">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-12 h-px" style={{ background: `linear-gradient(90deg, ${color} 0%, transparent 100%)` }} />
              <span className="text-[10.5px] font-mono uppercase tracking-[0.28em] font-bold" style={{ color: `${color}` }}>
                Funzione #{active + 1}
              </span>
            </div>

            <div
              className="w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center text-white mb-6"
              style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`, boxShadow: `0 14px 32px -8px ${color}AA` }}
            >
              <Icon className="w-7 h-7 md:w-9 md:h-9" strokeWidth={1.8} />
            </div>

            <h3
              style={{ fontFamily: "Inter, system-ui, sans-serif", fontWeight: 800, color: "#FFFFFF", letterSpacing: "-0.035em", lineHeight: 1.0, textShadow: "0 2px 14px rgba(0,0,0,0.4)" }}
              className="text-[34px] md:text-[52px] lg:text-[64px]"
            >
              {f.title}
            </h3>

            <p className="mt-5 max-w-[520px] text-[15px] md:text-[17px]" style={{ color: "rgba(255,255,255,0.88)", lineHeight: 1.55, textShadow: "0 2px 10px rgba(0,0,0,0.35)" }}>
              {f.desc}
            </p>

            {/* Static progress bar (no auto-advance) */}
            <div className="mt-8 h-[3px] w-full max-w-[280px] rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.15)" }}>
              <div className="h-full" style={{ width: "100%", background: `linear-gradient(90deg, ${color} 0%, ${color}99 100%)` }} />
            </div>
          </div>
        </article>

        {/* THUMB STRIP — all 25 functions for quick jump */}
        <div className="mt-6 md:mt-8">
          <div className="text-[10.5px] tracking-[0.22em] uppercase font-semibold mb-3" style={{ color: "#94A3B8" }}>
            Tutte le {FUNCTIONS.length} funzioni — clicca per saltare
          </div>
          <div ref={stripRef} className="flex gap-2.5 overflow-x-auto no-scrollbar pb-2" data-testid="functions-strip">
            {FUNCTIONS.map((fn, i) => {
              const isActive = i === active;
              const c = palette[i % palette.length];
              const Ic = fn.icon;
              return (
                <button
                  key={fn.title}
                  type="button"
                  onClick={() => { setActive(i); }}
                  data-testid={`function-thumb-${i}`}
                  className="group shrink-0 inline-flex items-center gap-2 px-3 h-10 rounded-full transition-all"
                  style={{
                    background: isActive ? `linear-gradient(135deg, ${c} 0%, ${c}DD 100%)` : "#FFFFFF",
                    border: isActive ? `1px solid ${c}` : "1px solid rgba(15,23,42,0.10)",
                    boxShadow: isActive ? `0 10px 28px -8px ${c}80` : "0 1px 2px rgba(15,23,42,0.04)",
                    color: isActive ? "#FFFFFF" : "#0F172A",
                  }}
                >
                  <Ic className="w-3.5 h-3.5" strokeWidth={2} />
                  <span className="text-[12px] font-semibold whitespace-nowrap">{fn.title}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   GOOGLE CALENDAR SHOWCASE — professional integration band
   ========================================================= */
function GoogleCalendarShowcase() {
  return (
    <section className="relative py-24 md:py-32" data-testid="gcal-showcase" style={{ background: "#F8FAFC" }}>
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-12 gap-8 items-center">
          {/* Text */}
          <div className="col-span-12 lg:col-span-5">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.20em] uppercase font-semibold mb-5 px-3 py-1.5 rounded-full"
                 style={{ color: "#7C3AED", background: "#EDE9FE" }}>
              <CalendarRange className="w-3 h-3" /> Integrazione nativa
            </div>
            <h2
              style={{ fontFamily: "Inter, system-ui, sans-serif", fontWeight: 800, color: "#0F172A", letterSpacing: "-0.035em", lineHeight: 1.05 }}
              className="text-[36px] md:text-[52px]"
            >
              Sincronizzato con
              <br />
              <span style={{ background: "linear-gradient(135deg, #3B82F6 0%, #7C3AED 100%)",
                WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                Google Calendar
              </span>.
            </h2>
            <p className="mt-6" style={{ color: "#475569", fontSize: 16.5, lineHeight: 1.6 }}>
              Le ferie, i turni, gli eventi aziendali e le trasferte appaiono automaticamente
              nel calendario personale di ogni dipendente. Sincronizzazione bidirezionale, in tempo reale.
            </p>
            <ul className="mt-8 space-y-3.5">
              {[
                "Eventi aziendali pushati in Google Calendar",
                "Ferie e permessi visibili al team",
                "Suggerimenti AI per evitare sovrapposizioni",
                "Compatibile con Outlook · iCal · Apple Calendar",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-[14.5px]" style={{ color: "#1E293B" }}>
                  <span className="mt-1 w-4 h-4 rounded-full flex items-center justify-center shrink-0"
                        style={{ background: "linear-gradient(135deg, #3B82F6 0%, #7C3AED 100%)" }}>
                    <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
            <div className="mt-9 flex items-center gap-4">
              <Link to="/register">
                <Button
                  data-testid="gcal-cta"
                  className="rounded-full h-11 px-6 text-[13.5px] font-semibold border-0 text-white"
                  style={{ background: "linear-gradient(135deg, #3B82F6 0%, #7C3AED 100%)" }}
                >
                  Attiva l'integrazione <ArrowRight className="ml-2 w-3.5 h-3.5" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Visual: Google Calendar UI mockup */}
          <div className="col-span-12 lg:col-span-7">
            <div className="relative rounded-3xl overflow-hidden"
                 style={{ background: "#FFFFFF", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 30px 80px -20px rgba(15,23,42,0.15)" }}>
              {/* Browser chrome */}
              <div className="flex items-center gap-2 px-5 h-10 border-b" style={{ borderColor: "rgba(15,23,42,0.06)" }}>
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#FF5F57" }} />
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#FEBC2E" }} />
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#28C840" }} />
                <div className="ml-4 px-3 py-1 rounded-md text-[11px] font-mono" style={{ background: "#F1F5F9", color: "#64748B" }}>
                  calendar.google.com — Verixa Sync
                </div>
              </div>

              {/* Mock GCal header */}
              <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: "rgba(15,23,42,0.06)" }}>
                <div className="flex items-center gap-3">
                  <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
                    <rect x="8" y="10" width="32" height="30" rx="3" fill="#FFFFFF" stroke="#1A73E8" strokeWidth="2"/>
                    <text x="24" y="32" fill="#1A73E8" fontSize="14" fontWeight="700" textAnchor="middle" fontFamily="Inter">27</text>
                    <rect x="8" y="10" width="32" height="6" fill="#1A73E8"/>
                  </svg>
                  <div>
                    <div style={{ fontWeight: 600, color: "#202124", fontSize: 16 }}>Maggio 2026</div>
                    <div style={{ color: "#5F6368", fontSize: 12 }}>Settimana lavorativa</div>
                  </div>
                </div>
                <div className="hidden sm:flex items-center gap-2">
                  <div className="px-3 py-1 rounded-full text-[11px] font-semibold" style={{ background: "#E8F0FE", color: "#1A73E8" }}>
                    ● Verixa connesso
                  </div>
                </div>
              </div>

              {/* Mock GCal grid */}
              <div className="grid grid-cols-5 text-[11px]" style={{ minHeight: 280 }}>
                {["Lun 25", "Mar 26", "Mer 27", "Gio 28", "Ven 29"].map((d, di) => (
                  <div key={d} className="border-r last:border-r-0 relative" style={{ borderColor: "rgba(15,23,42,0.06)" }}>
                    <div className="px-3 py-2.5 border-b text-center" style={{ borderColor: "rgba(15,23,42,0.06)", color: "#5F6368", fontWeight: 500 }}>
                      {d}
                    </div>
                    <div className="p-2 space-y-1.5 h-[240px] relative">
                      {/* Hour grid lines */}
                      {[0, 1, 2, 3, 4].map((h) => (
                        <div key={h} className="absolute left-0 right-0 border-t" style={{ borderColor: "rgba(15,23,42,0.04)", top: `${h * 48}px` }} />
                      ))}
                      {/* Events per day */}
                      {di === 0 && (
                        <div className="rounded-md px-2 py-1.5 text-[10px] font-semibold relative z-10"
                             style={{ background: "#3B82F615", color: "#1D4ED8", borderLeft: "3px solid #3B82F6" }}>
                          09:00 · Standup<br/>
                          <span className="opacity-70" style={{ fontWeight: 400 }}>Verixa · Team</span>
                        </div>
                      )}
                      {di === 1 && (
                        <>
                          <div className="rounded-md px-2 py-1.5 text-[10px] font-semibold relative z-10"
                               style={{ background: "#7C3AED15", color: "#5B21B6", borderLeft: "3px solid #7C3AED" }}>
                            10:30 · Demo cliente
                          </div>
                          <div className="rounded-md px-2 py-1.5 text-[10px] font-semibold relative z-10 mt-12"
                               style={{ background: "#06B6D415", color: "#0E7490", borderLeft: "3px solid #06B6D4" }}>
                            14:00 · Trasferta MI<br/>
                            <span className="opacity-70" style={{ fontWeight: 400 }}>Verixa · GPS</span>
                          </div>
                        </>
                      )}
                      {di === 2 && (
                        <div className="rounded-md px-2 py-1.5 text-[10px] font-semibold relative z-10 mt-6"
                             style={{ background: "#10B98115", color: "#047857", borderLeft: "3px solid #10B981" }}>
                          🌴 Ferie · Marco<br/>
                          <span className="opacity-70" style={{ fontWeight: 400 }}>Verixa HR</span>
                        </div>
                      )}
                      {di === 3 && (
                        <>
                          <div className="rounded-md px-2 py-1.5 text-[10px] font-semibold relative z-10"
                               style={{ background: "#3B82F615", color: "#1D4ED8", borderLeft: "3px solid #3B82F6" }}>
                            09:00 · Standup
                          </div>
                          <div className="rounded-md px-2 py-1.5 text-[10px] font-semibold relative z-10 mt-16"
                               style={{ background: "#F59E0B15", color: "#B45309", borderLeft: "3px solid #F59E0B" }}>
                            15:00 · Review HR<br/>
                            <span className="opacity-70" style={{ fontWeight: 400 }}>Verixa Analytics</span>
                          </div>
                        </>
                      )}
                      {di === 4 && (
                        <div className="rounded-md px-2 py-1.5 text-[10px] font-semibold relative z-10 mt-8"
                             style={{ background: "#7C3AED15", color: "#5B21B6", borderLeft: "3px solid #7C3AED" }}>
                          11:00 · Firma docs<br/>
                          <span className="opacity-70" style={{ fontWeight: 400 }}>Verixa eSign</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Sync footer */}
              <div className="flex items-center justify-between px-5 py-3 border-t" style={{ borderColor: "rgba(15,23,42,0.06)", background: "#F8FAFC" }}>
                <div className="flex items-center gap-2 text-[11.5px]" style={{ color: "#475569" }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Ultima sincronizzazione · 2 secondi fa
                </div>
                <div className="flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: "#7C3AED" }}>
                  <VerixaeLockup height={14} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   STATEMENT BAND — big editorial quote, light tone
   ========================================================= */
function StatementBand() {
  return (
    <section className="relative py-24 md:py-36 overflow-hidden" data-testid="statement">
      <div className="max-w-[1100px] mx-auto px-5 md:px-10 text-center">
        <div className="text-[11px] tracking-[0.24em] uppercase font-semibold mb-6" style={{ color: "#7C3AED" }}>— Manifesto</div>
        <h2
          style={{ fontFamily: "Inter, system-ui, sans-serif", fontWeight: 800, color: "#0F172A", letterSpacing: "-0.035em", lineHeight: 1.05 }}
          className="text-[36px] sm:text-[56px] md:text-[72px]"
        >
          La burocrazia è il rumore di fondo
          <span style={{ background: "linear-gradient(135deg, #3B82F6 0%, #7C3AED 100%)",
            WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            {" "}delle aziende mediocri
          </span>.
        </h2>
        <p className="mt-8 max-w-[600px] mx-auto" style={{ color: "#475569", fontSize: 17, lineHeight: 1.55 }}>
          Verixa rimuove l'attrito tra le persone e i loro processi. L'HR torna a essere
          uno strumento, non un secondo lavoro.
        </p>
      </div>
    </section>
  );
}

/* =========================================================
   STORIE — testimonials, NO company name
   ========================================================= */
function StorieCarousel() {
  const trackRef = useRef(null);
  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.7), behavior: "smooth" });
  };
  return (
    <section id="storie" className="relative py-24 md:py-32" data-testid="storie" style={{ background: "#FFFFFF" }}>
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div className="max-w-[700px]">
            <div className="text-[11px] tracking-[0.24em] uppercase font-semibold mb-4" style={{ color: "#7C3AED" }}>— Storie</div>
            <h2
              style={{ fontFamily: "Inter, system-ui, sans-serif", fontWeight: 800, color: "#0F172A", letterSpacing: "-0.035em", lineHeight: 1.05 }}
              className="text-[32px] sm:text-[48px] md:text-[60px]"
            >
              Persone che hanno
              <br />
              <span style={{ background: "linear-gradient(135deg, #3B82F6 0%, #7C3AED 100%)",
                WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                smesso di rincorrere
              </span> i fogli.
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              data-testid="storie-prev"
              className="w-11 h-11 rounded-full flex items-center justify-center transition-colors"
              style={{ background: "#FFFFFF", border: "1px solid rgba(15,23,42,0.10)", color: "#0F172A" }}
              aria-label="Precedente"
            ><ChevronLeft className="w-4 h-4" /></button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              data-testid="storie-next"
              className="w-11 h-11 rounded-full flex items-center justify-center transition-colors"
              style={{ background: "#FFFFFF", border: "1px solid rgba(15,23,42,0.10)", color: "#0F172A" }}
              aria-label="Successivo"
            ><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="relative -mx-5 md:mx-0">
          <div
            ref={trackRef}
            className="overflow-x-auto no-scrollbar snap-x snap-mandatory scroll-smooth flex gap-5 px-5 md:px-2 pb-2"
          >
            {STORIE.map((s) => (
              <article
                key={s.id}
                className="snap-start shrink-0 w-[300px] sm:w-[360px] md:w-[420px] rounded-3xl overflow-hidden"
                style={{ background: "#FFFFFF", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 12px 32px -16px rgba(15,23,42,0.10)" }}
                data-testid={`storia-${s.id}`}
              >
                <div className="aspect-[5/3] overflow-hidden relative" style={{ background: "#F1F5F9" }}>
                  <img src={s.img} alt={s.name} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <div className="p-6 md:p-7">
                  <p style={{ color: "#0F172A", fontSize: 17, lineHeight: 1.45, fontWeight: 500 }}>
                    "{s.quote}"
                  </p>
                  <div className="mt-5 pt-5 border-t" style={{ borderColor: "rgba(15,23,42,0.06)" }}>
                    <div style={{ color: "#0F172A", fontWeight: 700, fontSize: 14.5 }}>{s.name}</div>
                    <div style={{ color: "#64748B", fontSize: 12.5, marginTop: 2 }}>{s.role}</div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   STATS
   ========================================================= */
function StatsBand() {
  const stats = [
    { v: "1.200+", l: "Aziende attive" },
    { v: "47k",    l: "Dipendenti gestiti" },
    { v: "11s",    l: "Tempo medio rimborso" },
    { v: "99.98%", l: "Uptime 2025" },
  ];
  return (
    <section className="relative py-20 md:py-24" data-testid="stats-band" style={{ background: "#F8FAFC", borderTop: "1px solid rgba(15,23,42,0.06)", borderBottom: "1px solid rgba(15,23,42,0.06)" }}>
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-10 md:gap-y-0">
          {stats.map((s, i) => (
            <div key={`stat-${s.l}`} className={`text-center md:text-left ${i > 0 ? "md:border-l md:pl-10" : ""}`}
                 style={i > 0 ? { borderColor: "rgba(15,23,42,0.08)" } : {}}>
              <div
                style={{
                  fontFamily: "Inter, system-ui, sans-serif", fontWeight: 800, letterSpacing: "-0.04em",
                  background: "linear-gradient(135deg, #3B82F6 0%, #7C3AED 100%)",
                  WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent",
                }}
                className="text-[40px] md:text-[56px] leading-none"
              >
                {s.v}
              </div>
              <div className="mt-3" style={{ color: "#64748B", fontSize: 13.5, letterSpacing: "0.02em" }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   PRICING — Full grid with entrance animation (NO carousel)
   All 5 plans (4 tiers + Enterprise) fully visible on desktop.
   ========================================================= */
function PricingGrid({ onDemo }) {
  const [yearly, setYearly] = useState(false);
  const [plans, setPlans] = useState({});
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const sectionRef = useRef(null);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  // Reveal trigger on scroll
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          setRevealed(true);
          obs.disconnect();
        }
      });
    }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const handleBuy = async (tier) => {
    if (!user) {
      toast.info("Accedi per procedere all'acquisto");
      nav("/register");
      return;
    }
    const planId = `${tier}_${yearly ? "yearly" : "monthly"}`;
    try {
      const r = await api.post("/payments/checkout", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch {
      toast.error("Errore creazione checkout");
    }
  };

  const allCards = [
    ...TIERS,
    { id: "enterprise", name: "Enterprise", desc: "Oltre 50 dipendenti", seats: "SSO · SLA · onboarding dedicato",
      monthly: null, setup: null, perEmp: null, enterprise: true,
      features: ["SSO SAML / OIDC", "SLA 99.99% + supporto 24/7", "Customer Success dedicato", "Hosting privato disponibile", "Integrazioni custom"] },
  ];

  return (
    <section id="pricing" className="relative py-24 md:py-32" data-testid="pricing" style={{ background: "#FAFAF7" }} ref={sectionRef}>
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className={`text-center mb-12 transition-all duration-700 ${revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="text-[11px] tracking-[0.24em] uppercase font-semibold mb-4" style={{ color: "#3B82F6" }}>— Piani e prezzi</div>
          <h2
            style={{ fontFamily: "Inter, system-ui, sans-serif", fontWeight: 800, color: "#0F172A", letterSpacing: "-0.035em", lineHeight: 1.05 }}
            className="text-[36px] sm:text-[52px] md:text-[68px]"
          >
            Trasparenti.{" "}
            <span style={{ background: "linear-gradient(135deg, #3B82F6 0%, #7C3AED 100%)",
              WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              Senza sorprese
            </span>.
          </h2>
          <p className="mt-4 max-w-[560px] mx-auto" style={{ color: "#64748B", fontSize: 15.5 }}>
            Setup una tantum + canone mensile + €1 per ogni dipendente attivo. Nessun costo nascosto.
          </p>

          <div className="mt-8 inline-flex items-center gap-3 text-[13.5px] px-1.5 py-1.5 rounded-full" style={{ background: "#FFFFFF", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
            <button onClick={() => setYearly(false)} type="button" className="px-4 py-1.5 rounded-full transition-all"
              style={{ background: !yearly ? "#0F172A" : "transparent", color: !yearly ? "#FFFFFF" : "#64748B", fontWeight: 600 }}
              data-testid="pricing-monthly-btn">Mensile</button>
            <button onClick={() => setYearly(true)} type="button" className="px-4 py-1.5 rounded-full inline-flex items-center gap-2 transition-all"
              style={{ background: yearly ? "#0F172A" : "transparent", color: yearly ? "#FFFFFF" : "#64748B", fontWeight: 600 }}
              data-testid="pricing-yearly-btn">
              Annuale
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold text-white" style={{ background: "linear-gradient(135deg, #3B82F6 0%, #7C3AED 100%)" }}>-10%</span>
            </button>
          </div>
        </div>

        {/* GRID — all plans visible at once (responsive: 1/2/3/5 cols) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-5">
          {allCards.map((t, idx) => {
            const price = t.enterprise ? null : (yearly ? Math.round(t.monthly * 12 * 0.9) : t.monthly);
            const priceLabel = yearly ? "/anno" : "/mese";

            return (
              <article
                key={t.id}
                data-testid={`pricing-tier-${t.id}`}
                className="relative rounded-3xl p-6 md:p-7 transition-all duration-300 hover:-translate-y-2"
                style={{
                  background: t.highlight ? "linear-gradient(180deg, #0F172A 0%, #1E1B4B 100%)" : "#FFFFFF",
                  border: t.highlight
                    ? "1px solid rgba(255,255,255,0.10)"
                    : t.enterprise
                      ? "1px dashed rgba(15,23,42,0.20)"
                      : "1px solid rgba(15,23,42,0.08)",
                  boxShadow: t.highlight
                    ? "0 30px 80px -20px rgba(124,58,237,0.55)"
                    : "0 1px 2px rgba(15,23,42,0.04), 0 12px 32px -16px rgba(15,23,42,0.10)",
                  color: t.highlight ? "#FFFFFF" : "#0F172A",
                  // Entrance animation
                  opacity: revealed ? 1 : 0,
                  transform: revealed ? "translateY(0) scale(1)" : "translateY(40px) scale(0.96)",
                  transition: `opacity 700ms cubic-bezier(0.2,0.8,0.2,1) ${200 + idx * 110}ms, transform 700ms cubic-bezier(0.2,0.8,0.2,1) ${200 + idx * 110}ms, box-shadow 300ms, translate 300ms`,
                }}
              >
                {t.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.18em] uppercase text-white whitespace-nowrap"
                       style={{ background: "linear-gradient(135deg, #3B82F6 0%, #7C3AED 100%)" }}>
                    Più scelto
                  </div>
                )}

                <div style={{ color: t.highlight ? "rgba(255,255,255,0.6)" : "#94A3B8", fontSize: 10.5, letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 600 }}>
                  {t.desc}
                </div>
                <h3 className="mt-1.5" style={{ fontFamily: "Inter, system-ui, sans-serif", fontWeight: 800, fontSize: 26, letterSpacing: "-0.025em", color: t.highlight ? "#FFFFFF" : "#0F172A" }}>
                  {t.name}
                </h3>
                <div className="mt-1" style={{ color: t.highlight ? "rgba(255,255,255,0.65)" : "#64748B", fontSize: 12.5 }}>{t.seats}</div>

                {t.enterprise ? (
                  <>
                    <div className="mt-6" style={{ fontFamily: "Inter, system-ui, sans-serif", fontWeight: 800, fontSize: 30, letterSpacing: "-0.035em", color: "#0F172A", lineHeight: 1 }}>
                      Su richiesta
                    </div>
                    <div className="mt-1.5" style={{ color: "#94A3B8", fontSize: 11.5 }}>Preventivo in 24h</div>
                  </>
                ) : (
                  <>
                    <div className="mt-6 flex items-baseline gap-1.5">
                      <span style={{
                        fontFamily: "Inter, system-ui, sans-serif", fontWeight: 800, fontSize: 44, letterSpacing: "-0.04em", lineHeight: 1,
                        color: t.highlight ? "#FFFFFF" : "#0F172A",
                      }}>€{price}</span>
                      <span style={{ color: t.highlight ? "rgba(255,255,255,0.6)" : "#94A3B8", fontSize: 13 }}>{priceLabel}</span>
                    </div>
                    <div className="mt-1.5" style={{ color: t.highlight ? "rgba(255,255,255,0.5)" : "#94A3B8", fontSize: 11.5 }}>
                      + €{t.setup} setup · €{t.perEmp}/dip
                    </div>
                  </>
                )}

                <div className="my-5 h-px" style={{ background: t.highlight ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.08)" }} />

                <ul className="space-y-2.5 min-h-[160px]">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[12.5px]" style={{ color: t.highlight ? "rgba(255,255,255,0.88)" : "#1E293B" }}>
                      <span className="mt-0.5 w-3.5 h-3.5 rounded-full flex items-center justify-center shrink-0"
                            style={{ background: t.highlight ? "rgba(255,255,255,0.15)" : "linear-gradient(135deg, #3B82F6 0%, #7C3AED 100%)" }}>
                        <Check className="w-2 h-2 text-white" strokeWidth={3.5} />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => t.enterprise ? setQuoteOpen(true) : handleBuy(t.id)}
                  data-testid={t.enterprise ? "pricing-quote-btn" : `pricing-buy-${t.id}`}
                  className="mt-6 w-full rounded-full h-11 text-[13.5px] font-semibold border-0 text-white"
                  style={{
                    background: t.highlight
                      ? "linear-gradient(135deg, #3B82F6 0%, #7C3AED 100%)"
                      : "#0F172A",
                  }}
                >
                  {t.enterprise ? "Richiedi preventivo" : `Inizia con ${t.name}`}
                  <ArrowRight className="ml-2 w-3.5 h-3.5" />
                </Button>
              </article>
            );
          })}
        </div>

        <EnterpriseQuoteModal open={quoteOpen} onOpenChange={setQuoteOpen} />
      </div>
    </section>
  );
}

/* =========================================================
   FAQ
   ========================================================= */
function FAQ() {
  const items = [
    { q: "Posso provare Verixa prima di pagare?", a: "Sì. Ti offriamo una demo live di 30 minuti con un consulente Verixa, oltre a un periodo di prova guidato di 14 giorni con dati reali della tua azienda." },
    { q: "Come avviene l'export verso Zucchetti, TeamSystem o Inaz?", a: "Verixa genera CSV pre-formattati. Causali, totali e rimborsi sono mappati automaticamente." },
    { q: "I dati sono conformi al GDPR?", a: "Hosting su server EU, crittografia at-rest e in-transit, registro dei trattamenti e DPA firmabile in piattaforma." },
    { q: "Posso usare il GPS per validare le timbrature?", a: "Sì. Ogni sede ha un raggio configurabile (default 100m). Validazione automatica alla scansione QR." },
    { q: "Quanti dipendenti posso gestire?", a: "Da 5 a illimitati. Per oltre 50 dipendenti offriamo il piano Enterprise con SLA, SSO e onboarding assistito." },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="relative py-24 md:py-32" data-testid="faq" style={{ background: "#FFFFFF" }}>
      <div className="max-w-[1100px] mx-auto px-5 md:px-10">
        <div className="text-center mb-14">
          <div className="text-[11px] tracking-[0.24em] uppercase font-semibold mb-4" style={{ color: "#3B82F6" }}>— Domande</div>
          <h2
            style={{ fontFamily: "Inter, system-ui, sans-serif", fontWeight: 800, color: "#0F172A", letterSpacing: "-0.035em", lineHeight: 1.05 }}
            className="text-[32px] md:text-[56px]"
          >
            Risposte veloci.
          </h2>
        </div>
        <div className="border-t" style={{ borderColor: "rgba(15,23,42,0.08)" }}>
          {items.map((it, i) => (
            <div key={`faq-${it.q.slice(0, 24)}`} className="border-b" style={{ borderColor: "rgba(15,23,42,0.08)" }}>
              <button
                type="button"
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-start justify-between gap-6 py-6 text-left hover:opacity-90 transition-opacity"
                data-testid={`faq-${i}`}
              >
                <span className="flex items-start gap-5 flex-1">
                  <span className="text-[12px] mt-1.5 w-8 shrink-0" style={{ color: "#3B82F6", fontWeight: 700 }}>{String(i + 1).padStart(2, "0")}</span>
                  <span style={{ color: "#0F172A", fontSize: 18, lineHeight: 1.3, fontWeight: 600 }} className="md:text-[20px]">{it.q}</span>
                </span>
                <span className="shrink-0 mt-2" style={{ color: "#0F172A" }}>
                  {open === i ? <Minus className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                </span>
              </button>
              {open === i && (
                <div className="pb-7 pl-[3.25rem] pr-12" style={{ color: "#475569", fontSize: 15, lineHeight: 1.6 }}>
                  {it.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   FINAL CTA
   ========================================================= */
function FinalCTA({ onDemo }) {
  return (
    <section className="relative py-28 md:py-36 overflow-hidden" data-testid="final-cta" style={{ background: "#FAFAF7" }}>
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full opacity-40"
             style={{ background: "radial-gradient(circle, #DBEAFE 0%, transparent 60%)", filter: "blur(80px)" }} />
        <div className="absolute left-1/3 top-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full opacity-40"
             style={{ background: "radial-gradient(circle, #EDE9FE 0%, transparent 60%)", filter: "blur(80px)" }} />
      </div>
      <div className="relative max-w-[1100px] mx-auto px-5 md:px-10 text-center">
        <div className="text-[11px] tracking-[0.24em] uppercase font-semibold mb-6" style={{ color: "#7C3AED" }}>— Inizia oggi</div>
        <h2
          style={{ fontFamily: "Inter, system-ui, sans-serif", fontWeight: 800, color: "#0F172A", letterSpacing: "-0.04em", lineHeight: 0.95 }}
          className="text-[44px] sm:text-[72px] md:text-[100px]"
        >
          Quattordici giorni.
          <br />
          <span style={{ background: "linear-gradient(135deg, #3B82F6 0%, #7C3AED 100%)",
            WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            Zero impegni
          </span>.
        </h2>
        <p className="mt-8 max-w-[640px] mx-auto" style={{ color: "#475569", fontSize: 17, lineHeight: 1.5 }}>
          Prova ogni modulo. Setup guidato in 24 ore. Senza chiamate commerciali invadenti.
          Se non fa per voi, sparisce così com'è arrivato.
        </p>
        <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
          <Link to="/register">
            <Button
              data-testid="final-cta-primary"
              className="rounded-full h-14 px-8 text-[15px] font-semibold border-0 text-white"
              style={{ background: "linear-gradient(135deg, #3B82F6 0%, #7C3AED 100%)", boxShadow: "0 14px 40px -12px rgba(124,58,237,0.5)" }}
            >
              Inizia ora gratis <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
          <button
            onClick={onDemo}
            data-testid="final-cta-demo"
            className="text-[15px] font-medium inline-flex items-center gap-2 px-6 h-14 rounded-full transition-colors"
            style={{ color: "#0F172A", background: "#FFFFFF", border: "1px solid rgba(15,23,42,0.10)" }}
          >
            Parla con noi <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   FOOTER
   ========================================================= */
function Footer() {
  return (
    <footer className="relative pt-20 pb-12" style={{ background: "#FFFFFF", borderTop: "1px solid rgba(15,23,42,0.08)" }} data-testid="footer">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-12 gap-8 mb-16">
          <div className="col-span-12 md:col-span-6">
            <div className="mb-5">
              <VerixaeLockup height={40} />
            </div>
            <p className="max-w-[460px]" style={{ color: "#475569", fontSize: 18, lineHeight: 1.4, fontWeight: 500 }}>
              La piattaforma HR che non sembra una piattaforma HR.
            </p>
            <p className="mt-6 text-[11px] tracking-[0.22em] uppercase font-semibold" style={{ color: "#94A3B8" }}>Verixae · 2026</p>
          </div>

          <div className="col-span-6 md:col-span-2">
            <div className="text-[11px] tracking-[0.22em] uppercase font-semibold mb-4" style={{ color: "#94A3B8" }}>Prodotto</div>
            <ul className="space-y-2.5 text-[14px]" style={{ color: "#1E293B" }}>
              <li><a href="#servizi" className="hover:opacity-70 transition-opacity">Servizi</a></li>
              <li><a href="#storie" className="hover:opacity-70 transition-opacity">Storie</a></li>
              <li><a href="#pricing" className="hover:opacity-70 transition-opacity">Prezzi</a></li>
              <li><a href="#faq" className="hover:opacity-70 transition-opacity">FAQ</a></li>
            </ul>
          </div>

          <div className="col-span-6 md:col-span-2">
            <div className="text-[11px] tracking-[0.22em] uppercase font-semibold mb-4" style={{ color: "#94A3B8" }}>Azienda</div>
            <ul className="space-y-2.5 text-[14px]" style={{ color: "#1E293B" }}>
              <li className="cursor-pointer hover:opacity-70 transition-opacity">Manifesto</li>
              <li className="cursor-pointer hover:opacity-70 transition-opacity">Contatti</li>
              <li className="cursor-pointer hover:opacity-70 transition-opacity">Blog</li>
            </ul>
          </div>

          <div className="col-span-12 md:col-span-2">
            <div className="text-[11px] tracking-[0.22em] uppercase font-semibold mb-4" style={{ color: "#94A3B8" }}>Legale</div>
            <ul className="space-y-2.5 text-[14px]" style={{ color: "#1E293B" }}>
              <li><Link to="/privacy" className="hover:opacity-70 transition-opacity">Privacy · GDPR</Link></li>
              <li><Link to="/terms" className="hover:opacity-70 transition-opacity">Termini</Link></li>
              <li><Link to="/whistleblowing" className="hover:opacity-70 transition-opacity inline-flex items-center gap-1" data-testid="footer-whistleblowing"><Lock className="w-3 h-3" /> Whistleblowing</Link></li>
              <li>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/pitch-deck`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="footer-pitch-deck"
                  className="hover:opacity-70 transition-opacity inline-flex items-center gap-1"
                >
                  <FileText className="w-3 h-3" /> Pitch Deck PDF
                </a>
              </li>
              <li className="cursor-pointer hover:opacity-70 transition-opacity">Cookie</li>
            </ul>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 text-[11px] tracking-[0.18em] uppercase font-semibold pt-6" style={{ borderTop: "1px solid rgba(15,23,42,0.06)", color: "#94A3B8" }}>
          <span>© 2026 · @ninfa.eu</span>
          <span>P.IVA 18086141001</span>
          <span className="hidden md:block">Server EU · GDPR · Made in Italy</span>
          <span>v10.0</span>
        </div>
      </div>
    </footer>
  );
}

/* =========================================================
   PAGE — Light theme, body override
   ========================================================= */
export default function Landing() {
  const [demoOpen, setDemoOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "day";
    return window.localStorage?.getItem("verixae:theme") || "day";
  });
  const openDemo = () => setDemoOpen(true);
  const openTour = () => setTourOpen(true);
  const toggleTheme = () => {
    setTheme((t) => {
      const next = t === "day" ? "night" : "day";
      try { window.localStorage.setItem("verixae:theme", next); } catch {}
      return next;
    });
  };
  const nav = useNavigate();
  const { user } = useAuth();

  // Apply light landing scope to body
  useEffect(() => {
    document.body.classList.add("landing-light");
    return () => document.body.classList.remove("landing-light");
  }, []);

  // Day/Night theme class on body
  useEffect(() => {
    if (theme === "night") document.body.classList.add("landing-night");
    else document.body.classList.remove("landing-night");
    return () => document.body.classList.remove("landing-night");
  }, [theme]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.matchMedia?.("(display-mode: minimal-ui)")?.matches ||
      window.navigator?.standalone === true;
    if (!isStandalone) return;
    if (user) {
      const target = user.role === "employee" ? "/portal" : "/app";
      nav(target, { replace: true });
    } else {
      nav("/login", { replace: true });
    }
  }, [user, nav]);

  return (
    <div
      className="min-h-screen relative overflow-x-hidden"
      style={{ background: "#FAFAF7", color: "#0F172A", fontFamily: "Inter, system-ui, sans-serif" }}
      data-testid="landing-page"
    >
      <Nav onDemo={openDemo} theme={theme} onThemeToggle={toggleTheme} />
      <Hero onDemo={openDemo} onTour={openTour} />
      <TrustStrip />
      <ServicesExplainer />
      <FunctionsCarousel />
      <GoogleCalendarShowcase />
      <StatementBand />
      <StorieCarousel />
      <StatsBand />
      <PricingGrid onDemo={openDemo} />
      <FAQ />
      <FinalCTA onDemo={openDemo} />
      <Footer />
      <DemoRequestModal open={demoOpen} onOpenChange={setDemoOpen} source="demo" />
      <InteractiveTourModal open={tourOpen} onOpenChange={setTourOpen} />
      <BackToTop />
    </div>
  );
}
